import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, AlertTriangle, Plus, Minus, Trash2, BarChart2, CheckCircle, XCircle, Zap, RefreshCw, ChevronRight, Shield } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

async function apiFetch(token, path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Error ${res.status}`); }
  if (res.status === 204) return null;
  return res.json();
}

const STATUS_CFG = {
  out_of_stock:  { label: 'Out of Stock',    cls: 'text-red-400 bg-red-500/10 border-red-500/20',        bar: 'bg-red-500' },
  critical_low:  { label: 'Critical Low',    cls: 'text-orange-400 bg-orange-500/10 border-orange-500/20', bar: 'bg-orange-500' },
  below_target:  { label: 'Below Target',    cls: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', bar: 'bg-yellow-500' },
  ok:            { label: 'OK',              cls: 'text-green-400 bg-green-500/10 border-green-500/20',    bar: 'bg-green-500' },
};

// ── Set Targets Inline Form ───────────────────────────────────────────────────
function TargetEditor({ item, onSaved }) {
  const { token } = useAdminAuth();
  const [target, setTarget] = useState(String(item.target_stock || 0));
  const [reorder, setReorder] = useState(String(item.reorder_point || 0));
  const [batch, setBatch] = useState(String(item.production_batch_size || 5));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch(token, `/api/admin/inventory/${item.sku}/targets`, {
        method: 'PUT',
        body: JSON.stringify({ target_stock: +target, reorder_point: +reorder, production_batch_size: +batch }),
      });
      onSaved();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const inputCls = "w-16 bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-2 py-1.5 text-center outline-none";

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <span className="font-inter text-[10px] text-zinc-600 uppercase">Target</span>
        <input type="number" min="0" className={inputCls} value={target} onChange={e => setTarget(e.target.value)} />
      </div>
      <div className="flex items-center gap-1">
        <span className="font-inter text-[10px] text-zinc-600 uppercase">Reorder</span>
        <input type="number" min="0" className={inputCls} value={reorder} onChange={e => setReorder(e.target.value)} />
      </div>
      <div className="flex items-center gap-1">
        <span className="font-inter text-[10px] text-zinc-600 uppercase">Batch</span>
        <input type="number" min="1" className={inputCls} value={batch} onChange={e => setBatch(e.target.value)} />
      </div>
      <button onClick={handleSave} disabled={saving}
        className="font-inter text-[10px] bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-2 py-1.5 transition-colors">
        {saving ? '...' : 'Save'}
      </button>
    </div>
  );
}

// ── Add to Build Queue form ───────────────────────────────────────────────────
function AddToQueueBtn({ item, onAdded }) {
  const { token } = useAdminAuth();
  const [qty, setQty] = useState(item.suggested_restock || item.production_batch_size || 1);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const handleAdd = async () => {
    setAdding(true);
    try {
      await apiFetch(token, '/api/admin/inventory/build-queue', {
        method: 'POST',
        body: JSON.stringify({ sku: item.sku, quantity: +qty, reason: `Manual restock — ${item.stock_status}` }),
      });
      setAdded(true);
      onAdded();
    } catch (e) { alert(e.message); }
    setAdding(false);
  };

  return added ? (
    <span className="font-inter text-[10px] text-green-400 flex items-center gap-1"><CheckCircle size={11} />Added</span>
  ) : (
    <div className="flex items-center gap-1">
      <input type="number" min="1" value={qty} onChange={e => setQty(e.target.value)}
        className="w-12 bg-[#1e1e1e] border border-white/10 text-white font-inter text-xs px-1.5 py-1 text-center outline-none" />
      <button onClick={handleAdd} disabled={adding}
        data-testid={`add-to-queue-${item.sku}`}
        className="flex items-center gap-1 font-inter text-[10px] bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 px-2 py-1 transition-colors disabled:opacity-40">
        <Plus size={10} />{adding ? '...' : 'Queue'}
      </button>
    </div>
  );
}

// ── Main AdminInventory page ──────────────────────────────────────────────────
export default function AdminInventory() {
  const { token } = useAdminAuth();
  const [tab, setTab] = useState('overview');
  const [inventory, setInventory] = useState([]);
  const [summary, setSummary] = useState({});
  const [buildQueue, setBuildQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [error, setError] = useState('');
  const [bulkAdding, setBulkAdding] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [allowBackorder, setAllowBackorder] = useState(false);
  const [backorderSaving, setBackorderSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [inv, queue, settings] = await Promise.all([
        apiFetch(token, '/api/admin/inventory'),
        apiFetch(token, '/api/admin/inventory/build-queue'),
        apiFetch(token, '/api/admin/store-settings'),
      ]);
      setInventory(inv.inventory || []);
      setSummary(inv.summary || {});
      setBuildQueue(queue.items || []);
      setAllowBackorder(settings?.allow_backorder || false);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleBackorderToggle = async (newValue) => {
    setAllowBackorder(newValue);
    setBackorderSaving(true);
    try {
      await apiFetch(token, '/api/admin/store-settings', {
        method: 'PUT',
        body: JSON.stringify({ allow_backorder: newValue }),
      });
    } catch (e) { alert('Failed to save: ' + e.message); setAllowBackorder(!newValue); }
    setBackorderSaving(false);
  };

  const handleBulkRestock = async () => {
    setBulkAdding(true);
    try {
      const r = await apiFetch(token, '/api/admin/inventory/build-queue/bulk-restock', { method: 'POST' });
      await loadAll();
      if (r.added === 0) setError('All alert items already in build queue.');
      else setError(`Added ${r.added} items to build queue.`);
    } catch (e) { setError(e.message); }
    setBulkAdding(false);
  };

  const handleRemoveFromQueue = async (queueId) => {
    try {
      await apiFetch(token, `/api/admin/inventory/build-queue/${queueId}`, { method: 'DELETE' });
      setBuildQueue(prev => prev.filter(i => i.queue_id !== queueId));
    } catch (e) { alert(e.message); }
  };

  const handleUpdateQueueQty = async (queueId, qty) => {
    if (+qty <= 0) return;
    try {
      const updated = await apiFetch(token, `/api/admin/inventory/build-queue/${queueId}`, {
        method: 'PUT', body: JSON.stringify({ quantity: +qty })
      });
      setBuildQueue(prev => prev.map(i => i.queue_id === queueId ? updated : i));
    } catch (e) { alert(e.message); }
  };

  const alerts = inventory.filter(i => i.is_alert);
  const filteredInventory = categoryFilter === 'all' ? inventory : inventory.filter(i => i.category === categoryFilter);
  const pendingQueue = buildQueue.filter(i => i.status === 'pending');
  const inProductionQueue = buildQueue.filter(i => i.status === 'in_production');

  const inputCls = "bg-[#1e1e1e] border border-white/10 text-white font-inter text-sm px-3 py-2 outline-none focus:border-orange-500 transition-colors";

  return (
    <div data-testid="admin-inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">INVENTORY CONTROL</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Track stock levels, set targets, build to stock</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { setLoading(true); loadAll(); }}
            className="flex items-center gap-2 font-inter text-sm border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white px-4 py-2.5 transition-colors">
            <RefreshCw size={13} />Refresh
          </button>
          <Link to="/admin/production"
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors">
            <Zap size={14} />Go to Production
          </Link>
        </div>
      </div>

      {/* Backorder / Pre-order Toggle */}
      <div className="bg-[#1a1a1a] border border-white/10 p-4 mb-5 flex items-center justify-between">
        <div className="flex items-start gap-4">
          <div className={`mt-0.5 w-10 h-6 rounded-full relative cursor-pointer transition-colors flex-shrink-0 ${
            allowBackorder ? 'bg-orange-500' : 'bg-zinc-700'
          } ${backorderSaving ? 'opacity-60' : ''}`}
            data-testid="backorder-toggle"
            onClick={() => !backorderSaving && handleBackorderToggle(!allowBackorder)}
          >
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              allowBackorder ? 'translate-x-4' : 'translate-x-0.5'
            }`} />
          </div>
          <div>
            <p className="font-bebas text-base text-white tracking-wider">
              ALLOW OUT-OF-STOCK PURCHASES
              {backorderSaving && <span className="font-inter text-[10px] text-zinc-500 ml-2 tracking-normal">Saving...</span>}
            </p>
            <p className="font-inter text-xs text-zinc-500 mt-0.5">
              {allowBackorder
                ? 'ON — Customers can add items to cart and purchase even when inventory shows 0 in stock (pre-order / backorder mode)'
                : 'OFF — Items marked Out of Stock cannot be added to cart by customers (default)'
              }
            </p>
          </div>
        </div>
        <div className={`font-bebas text-sm tracking-widest px-3 py-1.5 border ${
          allowBackorder
            ? 'text-orange-400 border-orange-500/40 bg-orange-500/10'
            : 'text-zinc-500 border-white/10'
        }`}>
          {allowBackorder ? 'ENABLED' : 'DISABLED'}
        </div>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          {[
            { val: summary.total_skus, label: 'Total SKUs', color: 'text-white' },
            { val: summary.total_in_stock, label: 'Units In Stock', color: 'text-green-400' },
            { val: summary.total_reserved, label: 'Reserved (Orders)', color: 'text-blue-400' },
            { val: summary.nfa_in_stock, label: 'NFA In Stock', color: 'text-orange-400' },
            { val: summary.alert_count, label: 'Stock Alerts', color: summary.alert_count > 0 ? 'text-red-400' : 'text-zinc-500' },
          ].map(s => (
            <div key={s.label} className="bg-[#1a1a1a] border border-white/10 p-4 text-center">
              <div className={`font-bebas text-2xl tracking-wider ${s.color}`}>{s.val ?? 0}</div>
              <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-white/10">
        {[
          { key: 'overview', label: `All Stock (${inventory.length})` },
          { key: 'alerts', label: `Alerts (${alerts.length})`, urgent: alerts.length > 0 },
          { key: 'queue', label: `Build Queue (${pendingQueue.length})`, urgent: false },
        ].map(t => (
          <button key={t.key} data-testid={`inv-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 font-inter text-sm px-4 py-2.5 transition-colors border-b-2 -mb-px ${tab === t.key ? 'border-orange-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.label}
            {t.urgent && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-inter text-sm px-4 py-3 mb-4">
          {error}
          <button onClick={() => setError('')} className="ml-3 text-yellow-600 hover:text-yellow-400">✕</button>
        </div>
      )}

      {/* ===== TAB: OVERVIEW ===== */}
      {tab === 'overview' && (
        <div data-testid="inventory-overview-tab">
          {/* Category filter */}
          <div className="flex gap-2 flex-wrap mb-4">
            {['all', 'suppressors', 'sme', 'clamp-ons', 'muzzle-devices'].map(c => (
              <button key={c} onClick={() => setCategoryFilter(c)}
                className={`font-inter text-xs px-3 py-1.5 border transition-colors uppercase tracking-wider ${categoryFilter === c ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500 hover:border-white/30'}`}>
                {c === 'all' ? 'All Categories' : c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="bg-[#1a1a1a] border border-white/10 overflow-x-auto">
              <table className="w-full" data-testid="inventory-table">
                <thead>
                  <tr className="border-b border-white/10">
                    {['Product', 'In Stock', 'Reserved', 'Available', 'Target', 'Reorder At', 'Status', 'Stock Level', 'Actions'].map(h => (
                      <th key={h} className="px-3 py-3 text-left font-inter text-[10px] text-zinc-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredInventory.map(item => {
                    const cfg = STATUS_CFG[item.stock_status] || STATUS_CFG.ok;
                    const pct = item.target_stock > 0 ? Math.min(100, Math.round((item.available / item.target_stock) * 100)) : (item.available > 0 ? 100 : 0);
                    return (
                      <tr key={item.sku} data-testid={`inv-row-${item.sku}`} className="hover:bg-white/2 transition-colors">
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
                              <img src={item.image} alt="" className="w-full h-full object-contain p-0.5" onError={e => { e.target.style.display = 'none'; }} />
                            </div>
                            <div>
                              <p className="font-inter text-xs font-semibold text-white">{item.product_name}</p>
                              <p className="font-inter text-[10px] text-zinc-600">{item.sku}</p>
                              {item.nfa_item && <span className="font-inter text-[9px] text-orange-400">NFA</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center font-bebas text-base text-white">{item.in_stock}</td>
                        <td className="px-3 py-3 text-center font-inter text-sm text-blue-400">{item.reserved}</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`font-bebas text-lg ${item.available > 0 ? 'text-green-400' : 'text-red-400'}`}>{item.available}</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {editTarget === item.sku ? (
                            <input type="number" min="0" defaultValue={item.target_stock}
                              className="w-14 bg-[#1e1e1e] border border-orange-500 text-white text-xs text-center px-1 py-1 outline-none"
                              onBlur={async (e) => {
                                await apiFetch(token, `/api/admin/inventory/${item.sku}/targets`, {
                                  method: 'PUT', body: JSON.stringify({ target_stock: +e.target.value, reorder_point: item.reorder_point, production_batch_size: item.production_batch_size })
                                });
                                setEditTarget(null); loadAll();
                              }} autoFocus />
                          ) : (
                            <button onClick={() => setEditTarget(item.sku)}
                              className="font-inter text-xs text-zinc-400 hover:text-orange-400 transition-colors px-2 py-0.5 border border-transparent hover:border-orange-500/30">
                              {item.target_stock || '—'}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center font-inter text-xs text-zinc-500">{item.reorder_point || '—'}</td>
                        <td className="px-3 py-3">
                          <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>
                        </td>
                        <td className="px-3 py-3" style={{ minWidth: '80px' }}>
                          {item.target_stock > 0 ? (
                            <div>
                              <div className="h-1.5 bg-[#27272a] overflow-hidden">
                                <div className={`h-full ${cfg.bar} transition-all`} style={{ width: `${pct}%` }} />
                              </div>
                              <div className="font-inter text-[9px] text-zinc-600 mt-0.5">{pct}% of target</div>
                            </div>
                          ) : <span className="font-inter text-[10px] text-zinc-700">No target set</span>}
                        </td>
                        <td className="px-3 py-3">
                          <AddToQueueBtn item={item} onAdded={loadAll} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 bg-[#111111] border border-white/10 p-4">
            <p className="font-inter text-xs text-zinc-500">
              <strong className="text-zinc-300">How to set targets:</strong> Click any number in the "Target" or "Reorder At" column to edit it inline. 
              <strong className="text-zinc-300"> Target</strong> = desired stock level. 
              <strong className="text-zinc-300"> Reorder At</strong> = trigger alert when stock falls to this number. 
              <strong className="text-zinc-300"> Queue</strong> = adds to the production build queue.
            </p>
          </div>
        </div>
      )}

      {/* ===== TAB: ALERTS ===== */}
      {tab === 'alerts' && (
        <div data-testid="inventory-alerts-tab">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bebas text-2xl text-white tracking-wider">{alerts.length} STOCK ALERTS</h2>
              <p className="font-inter text-xs text-zinc-500 mt-0.5">Items below target or reorder point — add to build queue to replenish</p>
            </div>
            {alerts.length > 0 && (
              <button
                data-testid="bulk-restock-button"
                onClick={handleBulkRestock}
                disabled={bulkAdding}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bebas text-sm tracking-widest px-5 py-2.5 transition-colors"
              >
                <Zap size={14} />
                {bulkAdding ? 'ADDING...' : 'ADD ALL TO BUILD QUEUE'}
              </button>
            )}
          </div>

          {alerts.length === 0 ? (
            <div className="text-center py-16 bg-[#1a1a1a] border border-green-500/30">
              <CheckCircle size={40} className="text-green-500 mx-auto mb-3" />
              <p className="font-bebas text-xl text-green-400 tracking-wider">ALL STOCK LEVELS OK</p>
              <p className="font-inter text-sm text-zinc-600 mt-1">No products below target or reorder point. Set targets in the Overview tab to enable alerts.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map(item => {
                const cfg = STATUS_CFG[item.stock_status] || STATUS_CFG.below_target;
                return (
                  <div key={item.sku} data-testid={`alert-${item.sku}`} className="bg-[#1a1a1a] border border-white/10 p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
                      <img src={item.image} alt="" className="w-full h-full object-contain p-0.5" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-inter text-sm font-semibold text-white">{item.product_name}</span>
                        {item.nfa_item && <Shield size={11} className="text-orange-400" />}
                        <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-inter text-xs text-zinc-500">Available: <strong className={item.available === 0 ? 'text-red-400' : 'text-yellow-400'}>{item.available}</strong></span>
                        <span className="font-inter text-xs text-zinc-500">Target: <strong className="text-white">{item.target_stock}</strong></span>
                        <span className="font-inter text-xs text-zinc-500">Reorder at: <strong className="text-white">{item.reorder_point}</strong></span>
                        <span className="font-inter text-xs text-zinc-500">Suggested build: <strong className="text-orange-400">{item.suggested_restock}</strong></span>
                        {item.queued_for_production > 0 && (
                          <span className="font-inter text-xs text-blue-400">+{item.queued_for_production} queued</span>
                        )}
                      </div>
                    </div>
                    <AddToQueueBtn item={item} onAdded={loadAll} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== TAB: BUILD QUEUE ===== */}
      {tab === 'queue' && (
        <div data-testid="inventory-build-queue-tab">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bebas text-2xl text-white tracking-wider">INVENTORY BUILD QUEUE</h2>
              <p className="font-inter text-xs text-zinc-500 mt-0.5">Units to manufacture for stock replenishment — merged into cut sheet when production is run</p>
            </div>
            {pendingQueue.length > 0 && (
              <Link to="/admin/production"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-sm tracking-widest px-5 py-2.5 transition-colors">
                <Zap size={14} />GENERATE CUT SHEET →
              </Link>
            )}
          </div>

          {/* Pending items */}
          {pendingQueue.length > 0 ? (
            <div className="bg-[#1a1a1a] border border-orange-500/20 mb-5">
              <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                <span className="font-bebas text-lg text-white tracking-wider">PENDING — {pendingQueue.length} items · {pendingQueue.reduce((s, i) => s + i.quantity, 0)} units</span>
                <span className="font-inter text-xs text-orange-400">Will be included in the next cut sheet</span>
              </div>
              <div className="divide-y divide-white/5">
                {pendingQueue.map(item => (
                  <div key={item.queue_id} data-testid={`queue-item-${item.queue_id}`} className="flex items-center gap-4 px-5 py-3">
                    <div className="w-10 h-10 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
                      <img src={item.image} alt="" className="w-full h-full object-contain p-0.5" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-inter text-sm font-semibold text-white">{item.product_name}</span>
                        {item.nfa_item && <Shield size={11} className="text-orange-400" />}
                      </div>
                      <p className="font-inter text-xs text-zinc-500">{item.reason}</p>
                      <p className="font-inter text-[10px] text-zinc-700">Added by {item.added_by} · {item.added_at?.slice(0, 10)}</p>
                    </div>
                    {/* Qty editor */}
                    <div className="flex items-center border border-white/20">
                      <button onClick={() => handleUpdateQueueQty(item.queue_id, item.quantity - 1)} disabled={item.quantity <= 1}
                        className="px-2 py-1.5 text-zinc-500 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30">
                        <Minus size={11} />
                      </button>
                      <span className="px-3 py-1.5 font-bebas text-base text-white border-x border-white/20">{item.quantity}</span>
                      <button onClick={() => handleUpdateQueueQty(item.queue_id, item.quantity + 1)}
                        className="px-2 py-1.5 text-zinc-500 hover:text-white hover:bg-white/5 transition-colors">
                        <Plus size={11} />
                      </button>
                    </div>
                    <button onClick={() => handleRemoveFromQueue(item.queue_id)}
                      className="text-zinc-600 hover:text-red-400 transition-colors p-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              {/* Summary + action */}
              <div className="px-5 py-3 bg-[#141414] border-t border-white/10 flex items-center justify-between">
                <span className="font-inter text-sm text-zinc-400">
                  Total: <strong className="text-white">{pendingQueue.reduce((s, i) => s + i.quantity, 0)} units</strong> to manufacture for inventory
                </span>
                <Link to="/admin/production"
                  className="flex items-center gap-2 font-bebas text-sm tracking-widest text-orange-500 hover:text-white transition-colors">
                  Include in cut sheet → <ChevronRight size={14} />
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-[#1a1a1a] border border-white/10 mb-5">
              <Package size={36} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-lg text-zinc-600 tracking-wider">BUILD QUEUE IS EMPTY</p>
              <p className="font-inter text-sm text-zinc-600 mt-1">Add items from the Alerts tab or use the Queue button on any product row</p>
            </div>
          )}

          {/* In production */}
          {inProductionQueue.length > 0 && (
            <div className="bg-[#1a1a1a] border border-blue-500/20">
              <div className="px-5 py-3 border-b border-white/10">
                <span className="font-bebas text-lg text-blue-400 tracking-wider">IN PRODUCTION — {inProductionQueue.length} items</span>
                <span className="font-inter text-xs text-zinc-500 ml-3">Included in active cut sheet</span>
              </div>
              <div className="divide-y divide-white/5">
                {inProductionQueue.map(item => (
                  <div key={item.queue_id} className="flex items-center gap-4 px-5 py-3">
                    <div className="w-8 h-8 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
                      <img src={item.image} alt="" className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="flex-1">
                      <span className="font-inter text-sm text-zinc-300">{item.product_name}</span>
                      <span className="font-inter text-xs text-zinc-600 ml-3">{item.reason}</span>
                    </div>
                    <span className="font-bebas text-base text-blue-400">×{item.quantity}</span>
                    <span className="font-inter text-[10px] text-blue-400 border border-blue-500/20 bg-blue-500/10 px-2 py-0.5 uppercase tracking-wider">In Production</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
